import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { WipConfig } from '../../types';
import { useAccordionTableReducer } from '../useAccordionTableReducer';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditBankAccountModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditBankAccountModal';
import { PropertyTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/PropertyTable';

type BankAccountAccordionRowProps = {
  bankAccount: CreditAgreement.DomainEntity.Items.BankAccountEntity;
  wipConfig?: WipConfig;
  index: number;
  basePropertyKey: string;
  organizationId: string;
  setOpenedAccordionIndices: React.Dispatch<{
    isExpanded: boolean;
    index: number;
  }>;
  openedAccordionIndices: number[];
  columnWidth: number;
  showOptionalFields: Partial<
    Record<keyof CreditAgreement.DomainEntity.Items.BankAccountEntity, boolean>
  >;
};
export const BankAccountAccordionRow = ({
  bankAccount,
  wipConfig,
  index,
  basePropertyKey,
  organizationId,
  setOpenedAccordionIndices,
  openedAccordionIndices,
  columnWidth,
  showOptionalFields,
}: BankAccountAccordionRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const basePropertyKeyWithIdx = `${basePropertyKey}[${index}]`;
  const { entity, section, _isWrittenIn_ } = bankAccount;
  const { institutionName, name, routingNumber, accountNumber } = entity;

  const sanitizedBankAccountProperties = _.omitBy(
    _.omit(bankAccount, 'entity', '_isWrittenIn_', 'section'),
    _.isNil,
  );
  const bankAccountPropertyPairs = _.toPairs(sanitizedBankAccountProperties);

  const columnWidthStr = `${columnWidth}%`;
  const DropdownTd = () => {
    if (!bankAccountPropertyPairs.length) {
      return <Td width="5%" />;
    }
    if (_.includes(openedAccordionIndices, index)) {
      return (
        <Td width="5%">
          <ChevronUpIcon />
        </Td>
      );
    }
    return (
      <Td width="5%">
        <ChevronDownIcon />
      </Td>
    );
  };

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          <AccordionButton
            onClick={() =>
              setOpenedAccordionIndices({
                isExpanded,
                index,
              })
            }
            style={{ padding: 0 }}
          >
            <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
              <Tbody>
                <Tr>
                  <Td width={columnWidthStr}>{institutionName}</Td>
                  <Td width={columnWidthStr}>{name}</Td>
                  <Td width={columnWidthStr}>{routingNumber}</Td>
                  <Td width={columnWidthStr}>{accountNumber}</Td>
                  {showOptionalFields.section && (
                    <Td width={columnWidthStr}>{section || '-'}</Td>
                  )}
                  {_isWrittenIn_ && showOptionalFields._isWrittenIn_ && (
                    <Td width={columnWidthStr}>
                      {getBooleanSymbol(_isWrittenIn_)}
                    </Td>
                  )}
                  {wipConfig?.isMutateAllowed(basePropertyKey) && (
                    <Td width={columnWidthStr}>
                      <EditIconButton
                        ariaLabel="edit"
                        label="Edit"
                        onClick={(event) => {
                          setIsOpen(true);
                          if (event) {
                            event.stopPropagation();
                          }
                        }}
                      />
                      <DeleteArrayPropertyButton
                        wipConfig={wipConfig}
                        basePropertyKey={basePropertyKey}
                        idx={index}
                      />
                      {!getIn(wipConfig.wipShell, basePropertyKeyWithIdx) && (
                        <EditBankAccountModal
                          isOpen={isOpen}
                          setIsOpen={setIsOpen}
                          organizationId={organizationId}
                          basePropertyKey={basePropertyKeyWithIdx}
                          wipConfig={wipConfig}
                        />
                      )}
                    </Td>
                  )}
                  <DropdownTd />
                </Tr>
              </Tbody>
            </Table>
          </AccordionButton>

          <AccordionPanel paddingRight="0">
            <Box height="20px" />
            <Text fontSize="16px" mb="10px" fontWeight="bold">
              Bank account properties
            </Text>
            <PropertyTable
              propertyPairs={bankAccountPropertyPairs}
              basePropertyKey={basePropertyKeyWithIdx}
              wipConfig={wipConfig}
            />
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};

type BankAccountEntityAccordionTableProps = {
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  organizationId: string;
};
export const BankAccountEntityAccordionTable = ({
  bankAccounts,
  wipConfig,
  basePropertyKey,
  organizationId,
}: BankAccountEntityAccordionTableProps) => {
  const {
    openedAccordionIndices,
    setOpenedAccordionIndices,
  } = useAccordionTableReducer();

  const bankAccountEntityAccordionTableHeaders = [
    'Institution Name',
    'Name',
    'Routing Number',
    'Account Number',
  ];

  const showSection =
    _.filter(bankAccounts, (bankAccount) => {
      return !_.isNil(bankAccount.section);
    }).length > 0;
  const showIsWrittenIn =
    _.filter(bankAccounts, (bankAccount) => {
      return !_.isNil(bankAccount._isWrittenIn_);
    }).length > 0;
  if (showSection) {
    bankAccountEntityAccordionTableHeaders.push('Section');
  }
  if (showIsWrittenIn) {
    bankAccountEntityAccordionTableHeaders.push('Is Written In');
  }
  if (wipConfig) {
    bankAccountEntityAccordionTableHeaders.push('Action');
  }

  // Even column widths with fixed tableLayout so the headers and the accordion rows are always aligned. Dropdown column is fixed to 5%
  // The headers and rows have to be rendered in different tables because an accordion component cannot correctly wrap around an entire table row
  const columnWidth = 95 / bankAccountEntityAccordionTableHeaders.length;

  return (
    <>
      <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
        <Thead>
          <Tr>
            {_.map(bankAccountEntityAccordionTableHeaders, (header) => {
              return (
                <Th width={`${columnWidth}%`} key={`th-${header}`}>
                  {header}
                </Th>
              );
            })}
            <Th width="5%" />
          </Tr>
        </Thead>
      </Table>
      <Accordion allowMultiple index={openedAccordionIndices} pl="0">
        {_.map(bankAccounts, (bankAccount, index) => {
          return (
            <BankAccountAccordionRow
              key={`accordion-row-${bankAccount.entity.id}-${index}`}
              bankAccount={bankAccount}
              index={index}
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              organizationId={organizationId}
              setOpenedAccordionIndices={setOpenedAccordionIndices}
              openedAccordionIndices={openedAccordionIndices}
              showOptionalFields={{
                section: showSection,
                _isWrittenIn_: showIsWrittenIn,
              }}
              columnWidth={columnWidth}
            />
          );
        })}
      </Accordion>
    </>
  );
};
